"use client";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Accordion, Box, HStack, Icon } from "@chakra-ui/react";
import Link from "next/link";
import Image from "next/image";
import { Text } from "@/elements";
import { MAIN_ROUTES } from "@/constants/routes";
import { scrollToElement } from "@/utils/scrollToElement";
import { SIDEBAR_CONSTANTS } from "./sidebar.constants";

const PricingSidebar = () => {
  const background = "bg.panel";
  const searchParams = useSearchParams();
  const tableParam = searchParams?.get("table");

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (tableParam) {
      scrollToElement(tableParam as string, 120);
    }
  }, [tableParam]);

  useEffect(() => {
    const sectionIds = SIDEBAR_CONSTANTS.flatMap((item) =>
      item.options ? item.options.map((opt) => opt.value) : [item.value]
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveSection(id);
            setHasScrolled(true);
          }
        });
      },
      {
        rootMargin: "-120px 0px -50% 0px",
        threshold: 0.2,
      }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const activeKey = hasScrolled ? activeSection : tableParam;

  return (
    <Box
      bg={background}
      bottom={0}
      boxShadow="0 10px 10px 0 rgba(0,0,0,.1)"
      left={0}
      position="sticky"
      pt={2}
      top={24}
      width={{
        base: "0px",
        md: "18vw",
        xl: "20vw",
        "2xl": "320px",
      }}
    >
      <Accordion.Root collapsible w="full">
        {SIDEBAR_CONSTANTS.map((item, index) => {
          const { icon, label, image, hoverImage, options, value } = item;
          const isActiveItem = value === activeKey;
          const hasActiveChild = options?.some((opt) => opt.value === activeKey);

          return options ? (
            <Accordion.Item
              key={index}
              value={`item-${index}`}
              borderBottom="1px solid #E2E8F0"
            >
              <Accordion.ItemTrigger
                p={4}
                bg={hasActiveChild ? "#2b6cb0" : "transparent"}
                color={hasActiveChild ? "white" : "inherit"}
                _hover={{
                  background: "#2b6cb0",
                  color: "#f8fafc",
                  "& .sidebar-icon": {
                    color: "white",
                  },
                }}
              >
                <HStack flex="1" gap={3}>
                  <Icon
                    as={icon}
                    w={8}
                    h={8}
                    className="sidebar-icon"
                    color={hasActiveChild ? "white" : "#2b6cb0"}
                  />
                  {(image || hoverImage) && (
                    <Box
                      position="relative"
                      minW={{ base: "40px", xl: "50px" }}
                      minH={{ base: "40px", xl: "40px" }}
                      className="whiteImg"
                    >
                      {image && (
                        <Image
                          alt={label}
                          src={image}
                          fill
                          className="base-image"
                          style={{ objectFit: "contain" }}
                        />
                      )}
                      {hoverImage && (
                        <Image
                          alt={label}
                          src={hoverImage}
                          fill
                          className="hover-image"
                          style={{ objectFit: "contain" }}
                        />
                      )}
                    </Box>
                  )}
                  <Text variant="body2">{label}</Text>
                </HStack>
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>

              <Accordion.ItemContent px={4}>
                <Accordion.ItemBody pb={4}>
                  {options.map((option, subIndex) => {
                    const isActiveOption = option.value === activeKey;
                    return (
                      <Link
                        href={`${MAIN_ROUTES.PRICING}?table=${option.value}`}
                        key={subIndex}
                        replace
                        scroll={false}
                      >
                        <HStack
                          gap={2}
                          px={4}
                          py={4}
                          background={isActiveOption ? "#2b6cb0" : "transparent"}
                          color={isActiveOption ? "white" : "inherit"}
                          _hover={{
                            background: "gray.200",
                            color: "black",
                          }}
                        >
                          {option.image && (
                            <Image
                              src={option.image}
                              alt="image"
                              width={option.imageSize || 30}
                              height={option.imageSize || 30}
                              style={{
                                objectFit: "contain",
                              }}
                            />
                          )}
                          <Text cursor="pointer" variant="body3">
                            {option.label}
                          </Text>
                        </HStack>
                      </Link>
                    );
                  })}
                </Accordion.ItemBody>
              </Accordion.ItemContent>
            </Accordion.Item>
          ) : (
            <Link
              href={`${MAIN_ROUTES.PRICING}?table=${value}`}
              replace
              scroll={false}
              key={index}
            >
              <HStack
                flex="1"
                gap={3}
                p={4}
                className={`whiteImg ${isActiveItem ? "active-item" : ""}`}
                borderBottom="1px solid #E2E8F0"
                bg={isActiveItem ? "#2b6cb0" : "transparent"}
                color={isActiveItem ? "white" : "inherit"}
                _hover={{
                  background: "#2b6cb0",
                  color: "#f8fafc",
                  "& .sidebar-icon": {
                    color: "white",
                  },
                }}
              >
                {icon && (
                  <Icon
                    as={icon}
                    w={8}
                    h={8}
                    className="sidebar-icon"
                    color={isActiveItem ? "white" : "#2b6cb0"}
                  />
                )}
                {(image || hoverImage) && (
                  <Box
                    position="relative"
                    minW={{ base: "40px", xl: "50px" }}
                    minH={{ base: "40px", xl: "40px" }}
                    className="whiteImg"
                  >
                    {image && (
                      <Image
                        alt={label}
                        src={image}
                        fill
                        className="base-image"
                        style={{ objectFit: "contain" }}
                      />
                    )}
                    {(hoverImage || isActiveItem) && (
                      <Image
                        alt={label}
                        src={hoverImage}
                        fill
                        className="hover-image"
                        style={{ objectFit: "contain" }}
                      />
                    )}
                  </Box>
                )}
                <Text variant="body2">{label}</Text>
              </HStack>
            </Link>
          );
        })}
      </Accordion.Root>
    </Box>
  );
};

export default PricingSidebar;
